import { useState } from 'react'
import { useQuery } from 'react-query'
import { BarChart3, Users, FolderOpen, TrendingUp, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'
import { projetoService } from '../dominios/projetos/services/projeto'
import { formatDateToBrazilian } from '../utils/formatUtils'
import { Link } from 'react-router-dom'

function formatMoeda(valor: number | null | undefined): string {
  if (valor == null || Number.isNaN(valor)) return '-'
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function valorTotalOrcamento(projeto: any): number | null {
  const o = projeto?.orcamento
  if (!o) return null
  const escopo = Number(o.escopoOrcado) || 0
  const dev = Number(o.desenvolvimentoOrcado) || 0
  const testes = Number(o.testesOrcado) || 0
  const hom = Number(o.homologacaoCobrado) || 0

  const totalHoras = escopo + dev + testes + hom
  const valorHora = Number(projeto?.valorHoraAtual ?? projeto?.valorHora ?? projeto?.ValorHora ?? 0)

  if (totalHoras <= 0)
    return null

  const total = valorHora > 0 ? totalHoras * valorHora : totalHoras
  return total > 0 ? total : null
}

const TAMANHO_PAGINA_RECENTES = 5

function Dashboard() {
  const [paginaRecentes, setPaginaRecentes] = useState(1)

  const { data: resumo, isLoading: loadingResumo } = useQuery(
    ['dashboard-resumo-projetos'],
    () => projetoService.obterResumoDashboard(),
    { staleTime: 60000 }
  )

  const { data: recentes, isLoading: loadingRecentes } = useQuery(
    ['dashboard-projetos-recentes', paginaRecentes],
    () => projetoService.obterRecentesDashboard(paginaRecentes, TAMANHO_PAGINA_RECENTES),
    { keepPreviousData: true, staleTime: 30000 }
  )

  const totalAtual = resumo?.totalAtual ?? 0
  const percentualVariacao = resumo?.percentualVariacao ?? 0
  const resumoPorStatus = resumo?.resumoPorStatus ?? []
  const itemsRecentes = recentes?.items ?? []
  const totalCountRecentes = recentes?.totalCount ?? 0
  const totalPaginasRecentes = Math.ceil(totalCountRecentes / TAMANHO_PAGINA_RECENTES)
  const temRecentes = totalCountRecentes > 0

  const stats = [
    {
      title: 'Total de Projetos',
      value: loadingResumo ? '...' : String(totalAtual),
      change: loadingResumo ? '' : (percentualVariacao >= 0 ? `+${percentualVariacao}%` : `${percentualVariacao}%`),
      changeType: percentualVariacao >= 0 ? 'positive' : 'negative',
      icon: FolderOpen,
      color: 'bg-brand-500',
      resumoPorStatus: loadingResumo ? [] : resumoPorStatus
    },
    {
      title: 'Projetos Ativos',
      value: '8',
      change: '+3%',
      changeType: 'positive',
      icon: TrendingUp,
      color: 'bg-success-500'
    },
    {
      title: 'Equipe',
      value: '12',
      change: '+2',
      changeType: 'positive',
      icon: Users,
      color: 'bg-warning-500'
    },
    {
      title: 'Concluídos',
      value: '16',
      change: '+8%',
      changeType: 'positive',
      icon: BarChart3,
      color: 'bg-purple-500'
    }
  ]

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">Visão geral do sistema de gestão de projetos</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                {stat.change !== undefined && stat.change !== '' && (
                  <p className={`text-xs font-medium mt-1 flex items-center ${
                    stat.changeType === 'positive' ? 'text-success-600' : 'text-error-600'
                  }`}>
                    <TrendingUp className="w-3 h-3 mr-1" />
                    {stat.change} em relação ao mês passado
                  </p>
                )}
                {'resumoPorStatus' in stat && Array.isArray(stat.resumoPorStatus) && stat.resumoPorStatus.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {stat.resumoPorStatus.map((s: { statusCodigo: number; descricao: string; quantidade: number }) => (
                      <span
                        key={s.statusCodigo}
                        className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
                      >
                        {s.descricao}: {s.quantidade}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className={`w-12 h-12 flex-shrink-0 ${stat.color} rounded-lg flex items-center justify-center ml-3`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">Projetos Recentes</h3>
          <p className="text-sm text-gray-600 mt-1">Projetos em andamento (excluindo Pré Produção, Cancelado e Produção), ordenados por data de recebimento</p>
        </div>
        <div className="p-6">
          {loadingRecentes && (
            <div className="text-center py-8 text-gray-500">Carregando...</div>
          )}
          {!loadingRecentes && !temRecentes && (
            <div className="text-center py-8">
              <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum projeto recente</h3>
              <p className="text-gray-600">Os projetos em andamento aparecerão aqui.</p>
            </div>
          )}
          {!loadingRecentes && temRecentes && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-700">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-600 font-medium">
                      <th className="py-3 pr-4">Ticket</th>
                      <th className="py-3 pr-4">Nome</th>
                      <th className="py-3 pr-4">Status</th>
                      <th className="py-3 pr-4">Data recebimento</th>
                      <th className="py-3 pr-4">Analista SMN</th>
                      <th className="py-3 pr-4">Analista DDP</th>
                      <th className="py-3 pr-4">Valor total orçamento</th>
                      <th className="py-3">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsRecentes.map((projeto) => {
                      const totalOrc = valorTotalOrcamento(projeto)
                      return (
                        <tr key={projeto.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 pr-4 font-medium">{projeto.numeroTicket ?? '-'}</td>
                          <td className="py-3 pr-4">{projeto.nome ?? '-'}</td>
                          <td className="py-3 pr-4">{projeto.statusNome ?? '-'}</td>
                          <td className="py-3 pr-4">{projeto.dataRecebimento ? formatDateToBrazilian(projeto.dataRecebimento) : '-'}</td>
                          <td className="py-3 pr-4">{(projeto as any).analistaSMNNome ?? '-'}</td>
                          <td className="py-3 pr-4">{(projeto as any).analistaDDPNome ?? '-'}</td>
                          <td className="py-3 pr-4">{totalOrc != null ? formatMoeda(totalOrc) : '-'}</td>
                          <td className="py-3">
                            <Link
                              to="/projetos"
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-brand-600 hover:bg-brand-50 hover:text-brand-700 transition-colors"
                              title="Ir para projetos"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Link>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {totalPaginasRecentes > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    Exibindo {(paginaRecentes - 1) * TAMANHO_PAGINA_RECENTES + 1} a {Math.min(paginaRecentes * TAMANHO_PAGINA_RECENTES, totalCountRecentes)} de {totalCountRecentes}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPaginaRecentes((p) => Math.max(1, p - 1))}
                      disabled={paginaRecentes <= 1}
                      className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="flex items-center px-3 text-sm text-gray-700">
                      Página {paginaRecentes} de {totalPaginasRecentes}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPaginaRecentes((p) => Math.min(totalPaginasRecentes, p + 1))}
                      disabled={paginaRecentes >= totalPaginasRecentes}
                      className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default Dashboard
